import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import SectionProcessBg from '../components/SectionProcessBg';
import {
  IconBeaker,
  IconRecycle,
  IconRepeat,
  IconSeedling,
  IconShieldBan,
  IconTrendDown,
} from '../components/icons/BrandIcons';
import { EASE, staggerContainer } from '../motion/variants';

const IMPACT = [
  {
    v: '-42%',
    num: -42,
    suffix: '%',
    prefix: '',
    label: 'carbon vs. virgin TiO₂ route',
    accent: 'green',
    icon: IconTrendDown,
    iconKey: 'trend-down',
  },
  {
    v: '100%',
    num: 100,
    suffix: '%',
    prefix: '',
    label: 'recyclable-stream compatible',
    accent: 'gold',
    icon: IconRecycle,
    iconKey: 'recycle',
  },
  {
    v: '0',
    num: 0,
    suffix: '',
    prefix: '',
    label: 'added heavy-metal pigments',
    accent: 'navy',
    icon: IconShieldBan,
    iconKey: 'shield-ban',
  },
];

const CARDS = [
  {
    icon: IconRepeat,
    iconKey: 'repeat',
    title: 'Design for recycling',
    text: 'Mono-material masterbatch that survives mechanical recycling without staining the regrind.',
    accent: 'green',
  },
  {
    icon: IconSeedling,
    iconKey: 'seedling',
    title: 'Bio-attributed carriers',
    text: 'Mass-balance certified resin carriers cut cradle-to-gate emissions across the range.',
    accent: 'gold',
  },
  {
    icon: IconBeaker,
    iconKey: 'beaker',
    title: 'Resource efficiency',
    text: 'Higher pigment efficiency means lower dose, less waste and lighter packaging.',
    accent: 'blue',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

const cardIn = {
  hidden: { opacity: 0, y: 44, scale: 0.94 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.75, delay: i * 0.1, ease: EASE },
  }),
};

function ImpactValue({ item, active, reduceMotion }) {
  const [display, setDisplay] = useState(reduceMotion ? item.v : item.prefix + '0' + item.suffix);

  useEffect(() => {
    if (!active || reduceMotion) {
      setDisplay(item.v);
      return undefined;
    }

    if (item.num === 0) {
      setDisplay('0');
      return undefined;
    }

    const target = Math.abs(item.num);
    const isNeg = item.num < 0;
    const duration = 1400;
    const start = performance.now();

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - p) ** 3;
      const val = Math.round(eased * target);
      setDisplay(`${isNeg ? '-' : ''}${val}${item.suffix}`);
      if (p < 1) requestAnimationFrame(tick);
    };

    const frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, item, reduceMotion]);

  return display;
}

export default function SustainabilitySection({ fullPage = false }) {
  const reduceMotion = useReducedMotion();
  const metricsRef = useRef(null);
  const metricsInView = useInView(metricsRef, { once: true, amount: 0.35 });

  return (
    <section
      id="sustainability"
      className="sec sec-process-wrap sustainability-v2"
      data-section-theme="sustainability"
      data-palette="green"
    >
      <SectionProcessBg variant="eco" seed={6} />
      <div className="sustainability-v2__ambient" aria-hidden="true">
        <span className="sustainability-v2__orb sustainability-v2__orb--1" />
        <span className="sustainability-v2__orb sustainability-v2__orb--2" />
        <span className="sustainability-v2__scan" />
      </div>

      <div className="sec-process-content sustainability-v2__inner">
        <motion.div
          className="sustainability-v2__hdr"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={fadeUp}>
            <div className="kicker k-green">Our Commitment</div>
            <h2 className="title lt">
              A measurable path to
              <br />
              <em>circularity</em>
            </h2>
          </motion.div>
          {!fullPage && (
            <motion.p className="sustainability-v2__lead" variants={fadeUp}>
              Sustainability at Auralux is engineered into molecular structure — not printed on packaging.
            </motion.p>
          )}
        </motion.div>

        <div className="sustainability-v2__metrics" ref={metricsRef}>
          {IMPACT.map((item, i) => {
            const MetricIcon = item.icon;
            return (
            <motion.div
              key={item.label}
              className="sustainability-v2__metric"
              data-accent={item.accent}
              custom={i}
              variants={cardIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover={reduceMotion ? undefined : { y: -10, scale: 1.03 }}
            >
              <span className="sustainability-v2__metric-shine" aria-hidden="true" />
              <div className="sustainability-v2__metric-icon" data-icon={item.iconKey}>
                <MetricIcon size={28} />
              </div>
              <div className="sustainability-v2__metric-v">
                <ImpactValue item={item} active={metricsInView} reduceMotion={reduceMotion} />
              </div>
              <p className="sustainability-v2__metric-l">{item.label}</p>
            </motion.div>
            );
          })}
        </div>

        <motion.div
          className="sustainability-v2__cards"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.12 }}
        >
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.article
                key={card.title}
                className="sustainability-v2__card"
                data-accent={card.accent}
                variants={cardIn}
                custom={i}
                whileHover={
                  reduceMotion
                    ? undefined
                    : { y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 300, damping: 22 } }
                }
              >
                <span className="sustainability-v2__card-shine" aria-hidden="true" />
                <div className="sustainability-v2__card-icon" data-icon={card.iconKey}>
                  <Icon size={28} />
                </div>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </motion.article>
            );
          })}
        </motion.div>

        <motion.div
          className="sustainability-v2__cta"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <a href="/resources/auralux-sustainability-report.pdf" className="btn-p" download>
            Download 2026 Sustainability Report
          </a>
          {!fullPage && (
            <Link to="/sustainability" className="btn-g">
              Full ESG hub →
            </Link>
          )}
        </motion.div>
      </div>
    </section>
  );
}
